import { Box, Stack } from '@mui/material';
import { forwardRef, useEffect, useRef } from 'react';

import CloseIcon from '@vp/assets/icons/Close.svg?react';
import { VpIconButton } from '@vp/common/ui/component/VpIconButton';
import { VpImage } from '@vp/common/ui/component/VpImage';
import { VpMediaGradientOverlay } from '@vp/common/ui/component/VpMediaGradientOverlay';
import { ProfilePhotoModel } from '@vp/profile/core/model/ProfilePhotoModel';

export type ProfileImageScrollerProps = {
  selectedPhotoId: string;
  photos: ProfilePhotoModel[];
  close: () => void;
};

export const ProfileImageScroller = forwardRef<HTMLElement, ProfileImageScrollerProps>(({ selectedPhotoId, photos, close }, ref) => {
  const images = useRef<HTMLElement[]>([]);

  useEffect(() => {
    const index = photos.findIndex(({ id }) => selectedPhotoId === id);
    if (index !== -1) images.current[index]?.scrollIntoView();
  }, [selectedPhotoId, photos]);

  return (
    <Box
      ref={ref}
      autoFocus
      tabIndex={0}
      width={1}
      height={1}
      position="relative"
      sx={({ palette }) => ({ overflowY: 'auto', outline: 'none', backgroundColor: palette.background.default })}
    >
      <Stack gap={1}>
        {photos.map((photo, index) => (
          <VpMediaGradientOverlay key={photo.id} sx={{ height: '90svh' }}>
            <VpImage src={photo.url} hash={photo.hash} mode="contain" ref={el => void (images.current[index] = el!)} />
          </VpMediaGradientOverlay>
        ))}
      </Stack>

      <VpIconButton
        Icon={CloseIcon}
        sx={theme => ({
          p: 1,
          position: 'fixed',
          zIndex: theme.zIndex.mediaControls,
          top: { mobile: 0, tablet: theme.spacing(1) },
          left: { mobile: 0, tablet: theme.spacing(3) },
        })}
        onClick={close}
      />
    </Box>
  );
});

ProfileImageScroller.displayName = 'ProfileImageScroller';
